import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Contact } from '@/interfaces/contact'
import { Tag } from '@/interfaces/tag'
import { avatarName } from '@/utils/avatar-name'
import { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from './data-table-column-header'
import { DataTableRowActions } from './data-table-row-actions'

export const columns: ColumnDef<Contact>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label='Select all'
        className='translate-y-[2px]'
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label='Select row'
        className='translate-y-[2px]'
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'profilePicUrl',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Foto' />
    ),
    cell: ({ row }) => {
      return (
        <div className='flex space-x-2'>
          <Avatar className='w-8 h-8'>
            <AvatarImage src={row.getValue('profilePicUrl')} alt='Foto de perfil' />
            <AvatarFallback>
              {avatarName(row.getValue('name'))}
            </AvatarFallback>
          </Avatar>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Nome' />
    ),
    cell: ({ row }) => {
      return (
        <div className='flex space-x-2'>
          <span className='max-w-32 truncate font-medium sm:max-w-72 md:max-w-[31rem]'>
            {row.getValue('name')}
          </span>
        </div>
      )
    },
  },
  {
    accessorKey: 'phone',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Numero' />
    ),
    cell: ({ row }) => {
      return (
        <div className='flex w-[100px] items-center'>
          <span>{row.getValue('phone')}</span>
        </div>
      )
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Email' />
    ),
    cell: ({ row }) => {
      return (
        <div className='flex w-[100px] items-center'>
          <span>{row.getValue('email')}</span>
        </div>
      )
    },
  },
  {
    accessorKey: 'tags',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Segmentos' />
    ),
    cell: ({ row }) => {
      const segments = row.getValue<Tag[]>('tags')

      if (!segments) {
        return null
      }

      return (
        <div className='flex flex-wrap gap-2 items-center'>
          <span className='text-muted-foreground'>{segments[0]?.name}</span>
          {segments.length > 1 && (
            <HoverCard>
              <HoverCardTrigger>
                <Badge variant='outline' className='text-muted-foreground'>+ {segments.length - 1}</Badge>
              </HoverCardTrigger>
              <HoverCardContent>
                <ul className='list-none p-0 m-0'>
                  {segments.slice(1).map((segment) => (
                    <li key={segment.id}>{segment.name}</li>
                  ))}
                </ul>
              </HoverCardContent>
            </HoverCard>
          )}
        </div>
      )
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
    enableSorting: false,
  },
  {
    id: 'actions',
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
]
